/**
 * `/subscription-ended` — hard-lockout paywall (ADR-0047).
 *
 * Where the SPA lands after any tRPC call returns FORBIDDEN with
 * `cause.reason === "subscription-ended"`. Outside the `(workspace)` layout
 * group, matching the chrome-less pattern used by `/launch` and `/pos`:
 * no sidebar, no topbar, no section tabs. The only actions the user has are
 * "Reactivate my plan" (opens Razorpay Checkout via `billing.createSubscription`)
 * or "Talk to us" (WhatsApp).
 *
 * Uses only allowlisted tRPC procedures (`billing.overview`) — anything else
 * would bounce right back into this page because of the `accessBlocked` gate.
 *
 * @see apps/api/src/trpc.ts — the allowlist that keeps this page reachable
 */
import { useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Zap, MessageCircle, ArrowRight, XCircle, ShieldCheck } from "lucide-react";
import { trpc } from "../lib/trpc.ts";
import { PLAN_BY_CODE, formatPlanPrice, type PlanCode } from "@flint/api-contract";
import { useSubscriptionCheckout } from "../features/settings/billing/razorpay.ts";
import { TALK_URL } from "../features/settings/billing/billing-hero.tsx";

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  return new Date(iso + "T00:00:00Z").toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

function SubscriptionEndedPage() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  // Poll the local read-model every 2 s while the paywall is mounted. As soon
  // as the status flips off a locked state, we send the user back to `/launch`.
  const overview = trpc.billing.overview.useQuery({}, { refetchInterval: 2000 });
  // Poll Razorpay directly every 3 s until the local state is active. This
  // drives the actual `activateBilling` write when Razorpay confirms the
  // subscription is live — we don't rely on `useSubscriptionCheckout`'s
  // fire-and-forget poll (which only runs for ~55 s after the checkout modal
  // closes and dies with the component if the user reloads). `checkActivation`
  // is idempotent + cheap when nothing has changed. @see ADR-0047.
  const checkActivation = trpc.billing.checkActivation.useQuery({}, {
    refetchInterval: 3000,
    enabled: overview.data?.status !== "active",
  });
  const { activate, pendingPlan } = useSubscriptionCheckout();

  const data = overview.data;
  const planCode = (data?.plan ?? "growth") as PlanCode;
  const planEntry = PLAN_BY_CODE[planCode];
  const price = planEntry ? formatPlanPrice(planEntry) : null;
  const endedOn = formatDate(data?.currentPeriodEndsOn ?? null);
  const activating = pendingPlan === planCode;

  // When `checkActivation` flips the local row to active, invalidate overview
  // so the navigate-on-status effect below fires without waiting for the next
  // 2-s refetch tick.
  const activated = checkActivation.data?.activated ?? false;
  useEffect(() => {
    if (activated) void utils.billing.overview.invalidate();
  }, [activated, utils]);

  useEffect(() => {
    if (!data) return;
    // Any healthy state ⇒ let them in. `active` is the happy path after
    // re-subscription; `trialing` / `activation_required` cover the (rare)
    // case where a wayward navigate landed them here with a fresh trial.
    if (data.status === "active" || data.status === "trialing" || data.status === "activation_required") {
      void navigate({ to: "/launch" });
    }
  }, [data, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg px-6 py-12">
      <div className="w-full max-w-xl rounded-md border border-line border-l-[3px] border-l-bad bg-paper p-8 shadow-card">
        <div className="flex items-start gap-4">
          <span className="inline-grid h-10 w-10 shrink-0 place-items-center rounded-md bg-bad-50 text-bad">
            <XCircle size={19} strokeWidth={2} aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted">
              Subscription ended
            </p>
            <h1 className="mt-1 text-xl font-bold text-ink">
              {planEntry ? `${planEntry.name} plan is over` : "Your subscription is over"}
            </h1>
            <p className="mt-2 text-sm text-ink-2">
              {endedOn
                ? `Your billing period ended on ${endedOn}. The workspace is locked until you reactivate — your data is safe.`
                : "The workspace is locked until you reactivate — your data is safe."}
            </p>

            {price ? (
              <div className="mt-5 flex flex-col gap-2">
                <button
                  type="button"
                  disabled={activating}
                  onClick={() => void activate(planCode)}
                  className="inline-flex h-10 items-center gap-2 self-start rounded-sm bg-ink px-4 text-sm font-semibold text-paper hover:bg-ink-2 disabled:opacity-60"
                >
                  <Zap size={15} strokeWidth={2} aria-hidden />
                  {activating ? "Starting…" : `Reactivate · ${price}`}
                  {!activating && <ArrowRight size={15} aria-hidden />}
                </button>
                <a
                  href={TALK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-muted underline-offset-2 hover:underline"
                >
                  <MessageCircle size={12} aria-hidden />
                  Prefer to talk to us first?
                </a>
              </div>
            ) : (
              <a
                href={TALK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 inline-flex h-10 items-center gap-2 rounded-sm bg-ink px-4 text-sm font-semibold text-paper hover:bg-ink-2"
              >
                <MessageCircle size={15} strokeWidth={2} aria-hidden />
                Talk to us to reactivate
                <ArrowRight size={15} aria-hidden />
              </a>
            )}

            <p className="mt-6 inline-flex items-center gap-1.5 text-xs text-muted">
              <ShieldCheck size={13} aria-hidden />
              Your invoices, customers, and history are preserved. They come back the moment
              your plan is active again.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute("/subscription-ended")({
  component: SubscriptionEndedPage,
});
